import PropTypes from 'prop-types';
import React from 'react';
import { ChevronDownIcon, ChevronUpIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';

import { getElementFieldTypeIcon } from '@dashboard/services/pageFieldTypes';

import { DragIcon } from '@dashboard/icons/DragIcon';

import Button, { getButtonClass } from '@dashboard/components/Button';
import Collapsible from '@dashboard/components/Collapsible';
import Lozenge from '@dashboard/components/Lozenge';

import { useFieldEdit } from '../FieldEditContext';
import { useFieldHelpers } from '../useFieldHelpers';
import FieldEdit from './FieldEdit';

const propTypes = {
  field: PropTypes.object.isRequired,
  dragHandleProps: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

const FieldItem = ({ field, dragHandleProps, disabled }) => {
  const { showRemoveField, onRemoveField } = useFieldHelpers(field._id || field.key);
  const { isAddingField, setIsEditingField } = useFieldEdit();

  return (
    <div className="w-full">
      <Collapsible.Root
        open={field.isEditing}
        onOpenChange={open => {
          if (field.isNew) return;

          setIsEditingField(open, field._id);
        }}
      >
        <div className="w-full sticky top-0 bg-white z-10">
          <div className="flex flex-row gap-2 items-center justify-start">
            <div {...dragHandleProps}>
              <DragIcon className="w-4 h-4" />
            </div>

            <Collapsible.Trigger
              className={clsx(
                `py-3 font-medium truncate w-full text-left flex items-center justify-between !ring-0 !focus:ring-0`,
                getButtonClass('bare'),
                {
                  '!text-gray-900 !hover:text-gray-900 cursor-not-allowed': isAddingField, // remove hover effect when adding variant
                },
              )}
              disabled={field.isNew}
              type="button"
            >
              <div className="inline-flex gap-x-2 items-center truncate">
                <div>{getElementFieldTypeIcon(field.element)}</div>

                <div className="truncate">
                  {field.label} <span>{field.required ? '*' : ''}</span>
                  {field.hidden && <Lozenge className="ml-2">Hidden</Lozenge>}
                  {field.showHideLogic?.enabled && <Lozenge className="ml-2">Conditional</Lozenge>}
                </div>
              </div>

              <span>
                {field.isEditing ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </span>
            </Collapsible.Trigger>

            {showRemoveField && (
              <div className="ml-auto flex gap-2">
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild disabled={isAddingField}>
                    <Button
                      aria-label="more options"
                      appearance="icon-button"
                      size="narrow"
                      disabled={isAddingField}
                    >
                      <EllipsisVerticalIcon className="w-4 h-4" />
                    </Button>
                  </DropdownMenu.Trigger>

                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      className="origin-top-right absolute right-0 z-20 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-gray-100 focus:outline-none overflow-hidden"
                      side="bottom"
                      align="end"
                    >
                      <DropdownMenu.Item
                        onClick={onRemoveField}
                        className="p-2 outline-none hover:bg-gray-100 cursor-pointer"
                      >
                        Delete
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </div>
            )}
          </div>

          {field.error && (
            <div
              style={{
                marginTop: '0.5em',
                color: 'var(--gray-darker)',
              }}
            >
              {field.error}
            </div>
          )}
        </div>

        <Collapsible.Content className={clsx(`pb-4 pt-2 relative`)}>
          <FieldEdit field={field} disabled={disabled} fieldKey={field._id || field.key} />
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
};

FieldItem.propTypes = propTypes;

export default FieldItem;
